import type { PhotoCategory } from '@areapif/areapif-shared'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useEvidenceUpload } from '../../hooks'
import type { Locale } from '../../i18n'
import { getTranslate } from '../../i18n'

interface AddPhotosDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  inspectionId: string
  locale: Locale
  availableCategories: readonly PhotoCategory[]
}

export const AddPhotosDialog: React.FC<AddPhotosDialogProps> = ({
  open,
  onClose,
  onSuccess,
  inspectionId,
  locale,
  availableCategories,
}) => {
  const t = getTranslate(locale)
  const { uploadPhotos, isUploading, error } = useEvidenceUpload()

  const [files, setFiles] = useState<File[]>([])
  const [category, setCategory] = useState<PhotoCategory | ''>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetState = useCallback(() => {
    setFiles([])
    setCategory('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  useEffect(() => {
    if (open) {
      resetState()
    }
  }, [open, resetState])

  const handleClose = useCallback(() => {
    if (!isUploading) {
      resetState()
      onClose()
    }
  }, [isUploading, resetState, onClose])

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files
    if (selected) {
      setFiles(Array.from(selected))
    }
  }, [])

  const handleCategoryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCategory(event.target.value as PhotoCategory)
  }, [])

  const handleUpload = useCallback(async () => {
    if (files.length === 0 || category === '') return

    try {
      await uploadPhotos(inspectionId, files, category)
      resetState()
      onSuccess()
      onClose()
    } catch {
      // error is surfaced via the hook's error state
    }
  }, [files, category, inspectionId, uploadPhotos, resetState, onSuccess, onClose])

  const canUpload = files.length > 0 && category !== '' && !isUploading

  const fileSelectionLabel =
    files.length === 0
      ? t('pages.historical_inspections.add_evidence.no_files')
      : files.length === 1
        ? (files[0]?.name ?? '')
        : `${String(files.length)} ${t('pages.historical_inspections.add_evidence.select_files').toLowerCase()}`

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('pages.historical_inspections.add_evidence.dialog_title_photos')}</DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{t('pages.historical_inspections.add_evidence.error')}</Alert>}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" component="label" disabled={isUploading}>
              {t('pages.historical_inspections.add_evidence.select_files')}
              <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={handleFileChange} />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              {fileSelectionLabel}
            </Typography>
          </Box>

          <TextField
            select
            label={t('pages.historical_inspections.add_evidence.category')}
            value={category}
            onChange={handleCategoryChange}
            disabled={isUploading}
            fullWidth
          >
            {availableCategories.map(value => (
              <MenuItem key={value} value={value}>
                {t(`resources.historical_inspections.enums.photo_category.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          {t('pages.historical_inspections.add_evidence.cancel')}
        </Button>
        <Button
          onClick={() => {
            void handleUpload()
          }}
          variant="contained"
          disabled={!canUpload}
          startIcon={isUploading ? <CircularProgress size={16} /> : undefined}
        >
          {isUploading ? t('pages.historical_inspections.add_evidence.uploading') : t('pages.historical_inspections.add_evidence.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddPhotosDialog
